import { useRef, useEffect, useCallback } from "react";

/**
 * IntersectionObserver-based autoplay hook for videos.
 * Plays the video when it's visible in the viewport and pauses when it's not.
 * Returns a ref to attach to the <video> element.
 */
export const useInViewAutoplay = (threshold = 0.3) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const video = videoRef.current;
        if (!video) return;

        if (entry.isIntersecting) {
          video.play().catch(() => {
            // Autoplay may be blocked by browser policy — ignore silently
          });
        } else {
          video.pause();
        }
      });
    },
    []
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
    });

    observer.observe(video);
    return () => observer.disconnect();
  }, [handleIntersection, threshold]);

  return videoRef;
};
